// UserAvatar Component - Reusable avatar display with upload functionality
// Supports multiple sizes, upload progress, and image processing
// Uses API-based upload mechanism with signed URLs

import { useState, useRef, useCallback } from 'react';
import { avatarUploadService } from '../../services/avatarUpload';
import type { PhotoUrls } from '../../lib/types/user';

export interface UserAvatarProps {
  // Display properties
  displayName: string;
  photoUrls?: PhotoUrls | null;
  size?: 'sm' | 'md' | 'lg' | 'xl';

  // Upload properties
  editable?: boolean;
  userId?: string;
  onUploadComplete?: (photoUrls: PhotoUrls) => void;
  onUploadError?: (error: Error) => void;

  // Style properties
  className?: string;
}

const sizeClasses = {
  sm: 'h-8 w-8 text-sm',
  md: 'h-12 w-12 text-lg',
  lg: 'h-16 w-16 text-xl',
  xl: 'h-20 w-20 text-2xl',
};

const ringClasses = {
  sm: 'ring-2',
  md: 'ring-2',
  lg: 'ring-4',
  xl: 'ring-4',
};

export function UserAvatar({
  displayName,
  photoUrls,
  size = 'md',
  editable = false,
  userId,
  onUploadComplete,
  onUploadError,
  className = '',
}: UserAvatarProps) {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Get the appropriate photo URL based on size
  const photoUrl = photoUrls?.['512'] ?? photoUrls?.['128'] ?? null;
  const displayUrl = previewUrl ?? photoUrl;

  // Generate initials from display name
  const initials = displayName
    .split(' ')
    .map(word => word.charAt(0))
    .join('')
    .toUpperCase()
    .slice(0, 2);

  // Handle file selection
  const handleFileSelect = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file || !userId) return;

      try {
        setIsUploading(true);
        setUploadProgress(0);

        // Create a preview URL
        const preview = URL.createObjectURL(file);
        setPreviewUrl(preview);

        // Set up progress tracking
        avatarUploadService.onProgress(progress => {
          setUploadProgress(progress);
        });

        // Upload original image and wait for backend processing
        const photoUrls = await avatarUploadService.uploadAvatar(file, userId);

        // Cleanup preview URL
        URL.revokeObjectURL(preview);
        setPreviewUrl(null);

        // Notify parent component with the processed photo URLs
        onUploadComplete?.(photoUrls);
      } catch (error) {
        console.error('Avatar upload error:', error);
        onUploadError?.(
          error instanceof Error ? error : new Error('Failed to upload avatar')
        );

        // Cleanup on error
        if (previewUrl) {
          URL.revokeObjectURL(previewUrl);
          setPreviewUrl(null);
        }
      } finally {
        setIsUploading(false);
        setUploadProgress(0);

        // Reset file input
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    },
    [userId, onUploadComplete, onUploadError, previewUrl]
  );

  // Handle drag and drop
  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (!isUploading && editable) {
        setIsDragOver(true);
      }
    },
    [isUploading, editable]
  );

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    async (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);

      if (!editable || isUploading || !userId) return;

      const file = e.dataTransfer.files?.[0];
      if (!file) return;

      // Create a synthetic event to reuse handleFileSelect
      const syntheticEvent = {
        target: { files: [file] },
      } as unknown as React.ChangeEvent<HTMLInputElement>;

      await handleFileSelect(syntheticEvent);
    },
    [editable, isUploading, userId, handleFileSelect]
  );

  const avatarContent = displayUrl ? (
    <img
      src={displayUrl}
      alt={displayName}
      className={`${sizeClasses[size]} rounded-full object-cover ${ringClasses[size]} ring-gray-200 dark:ring-gray-700`}
    />
  ) : (
    <div
      className={`${sizeClasses[size]} rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-semibold ${ringClasses[size]} ring-gray-200 dark:ring-gray-700`}
    >
      {initials}
    </div>
  );

  if (!editable) {
    return (
      <div className={className} data-testid="user-avatar">
        {avatarContent}
      </div>
    );
  }

  return (
    <div
      className={`relative inline-block ${className}`}
      data-testid="user-avatar"
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={e => void handleDrop(e)}
    >
      {avatarContent}

      {/* Upload overlay */}
      {editable && (
        <button
          type="button"
          onClick={() => {
            fileInputRef.current?.click();
          }}
          disabled={isUploading}
          className={`absolute inset-0 ${sizeClasses[size]} rounded-full bg-black bg-opacity-0 hover:bg-opacity-50 transition-opacity flex items-center justify-center group ${
            isDragOver ? 'bg-opacity-50' : ''
          }`}
          aria-label="Upload new avatar"
        >
          <div className="opacity-0 group-hover:opacity-100 transition-opacity">
            {isUploading ? (
              <div className="text-white text-xs font-medium">
                {Math.round(uploadProgress)}%
              </div>
            ) : (
              <svg
                className="w-6 h-6 text-white"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
              </svg>
            )}
          </div>
        </button>
      )}

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/jpg,image/png,image/webp"
        onChange={e => void handleFileSelect(e)}
        disabled={isUploading}
        className="hidden"
        aria-label="Choose avatar file"
      />

      {/* Upload progress indicator */}
      {isUploading && (
        <div className="absolute inset-0 rounded-full pointer-events-none">
          <svg className={`${sizeClasses[size]} transform -rotate-90`}>
            <circle
              cx={parseInt(sizeClasses[size].split(' ')[0].slice(2)) * 2}
              cy={parseInt(sizeClasses[size].split(' ')[0].slice(2)) * 2}
              r={parseInt(sizeClasses[size].split(' ')[0].slice(2)) * 2 - 2}
              stroke="currentColor"
              strokeWidth="4"
              fill="none"
              className="text-gray-200 dark:text-gray-700"
            />
            <circle
              cx={parseInt(sizeClasses[size].split(' ')[0].slice(2)) * 2}
              cy={parseInt(sizeClasses[size].split(' ')[0].slice(2)) * 2}
              r={parseInt(sizeClasses[size].split(' ')[0].slice(2)) * 2 - 2}
              stroke="currentColor"
              strokeWidth="4"
              fill="none"
              strokeDasharray={`${2 * Math.PI * (parseInt(sizeClasses[size].split(' ')[0].slice(2)) * 2 - 2)}`}
              strokeDashoffset={`${2 * Math.PI * (parseInt(sizeClasses[size].split(' ')[0].slice(2)) * 2 - 2) * (1 - uploadProgress / 100)}`}
              className="text-blue-600 dark:text-blue-400 transition-all duration-300"
            />
          </svg>
        </div>
      )}
    </div>
  );
}
